import React, { useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../../../../components/Modal';
import { Button, Select } from '../../../../components';
import { InitialsAvatar } from '../shared';
import { useDutyRoster } from '../DutyRosterContext';

/**
 * Inline "delete this team?" bar — shown in place of the team header, so there
 * is no second window stacked on this one. Staff go to Unassigned; their
 * shifts and history are kept.
 */
export const DeleteTeamConfirm = ({ team, staffCount, onCancel, onDeleted }) => {
    const { deleteTeam } = useDutyRoster();
    const [busy, setBusy] = useState(false);
    const confirm = async () => {
        setBusy(true);
        try {
            await deleteTeam(team);
            onDeleted?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not delete the team.');
            setBusy(false);
        }
    };
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-rose-50 dark:bg-rose-900/10 border-b border-rose-200 dark:border-rose-900/50">
            <p className="text-[12px] text-rose-700 dark:text-rose-300 min-w-0">
                Delete <strong>{team.name}</strong>?{' '}
                {staffCount > 0 ? `Its ${staffCount} staff move to Unassigned.` : 'It has no staff.'} Shifts and history are kept.
            </p>
            <div className="flex items-center gap-2 shrink-0">
                <Button type="button" size="sm" variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button type="button" size="sm" variant="danger" onClick={confirm} loading={busy}>Delete team</Button>
            </div>
        </div>
    );
};

const ManageTeamsModal = () => {
    const { groups, manageTeamsOpen, manageStaff, manageMoves, manageLoading, manageSearch, setManageSearch, savingMoves, draggedMemberId, setDraggedMemberId, dragOverTeam, setDragOverTeam, closeManageTeams, setMemberTeam, submitManageTeams, teamColor } = useDutyRoster();
    const [confirmingId, setConfirmingId] = useState(null); // team asking "delete?"
    useEffect(() => { if (!manageTeamsOpen) setConfirmingId(null); }, [manageTeamsOpen]);
    return (
            <Modal
                isOpen={manageTeamsOpen}
                onClose={closeManageTeams}
                title="Manage teams"
                maxWidth="max-w-[680px]"
            >
                {(() => {
                    const search = manageSearch.toLowerCase().trim();
                    const matches = (s) => s.name.toLowerCase().includes(search) || (s.user_id || '').toLowerCase().includes(search);
                    // Render each team (then an "Unassigned" bucket). A carer sits
                    // under their PENDING team (manageMoves), so drag/drop and the
                    // dropdown both relocate the card live before saving.
                    const buckets = [...groups.map((g, gi) => ({ id: g.id, name: g.name, color: teamColor(g, gi) })), { id: '', name: 'Unassigned', color: '#94A3B8' }];
                    return (
                        <div className="space-y-4 pt-1">
                            <p className="text-[12px] text-[#64748B] dark:text-gray-400 -mt-1">Drag a carer onto another team — or use the dropdown. Their shifts follow them to the new team.</p>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
                                <input
                                    type="text"
                                    value={manageSearch}
                                    onChange={(e) => setManageSearch(e.target.value)}
                                    placeholder="Search staff..."
                                    className="pl-9 pr-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-gray-700 focus:border-blue-500 outline-none text-[13px] w-full bg-white dark:bg-[#31373f] dark:text-gray-200"
                                />
                            </div>

                            {manageLoading ? (
                                <div className="py-10 text-center text-[13px] text-[#94A3B8] dark:text-gray-500">Loading teams & staff…</div>
                            ) : (
                                <div className="space-y-3 max-h-[58vh] overflow-y-auto pr-1">
                                    {buckets.map((team) => {
                                        const members = manageStaff.filter((s) => String(manageMoves[s.id] ?? '') === String(team.id ?? '')).filter(matches);
                                        const isDropTarget = dragOverTeam === team.id;
                                        return (
                                            <div
                                                key={team.id || 'unassigned'}
                                                onDragOver={(e) => { e.preventDefault(); if (dragOverTeam !== team.id) setDragOverTeam(team.id); }}
                                                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTeam(undefined); }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    if (draggedMemberId != null) setMemberTeam(draggedMemberId, team.id);
                                                    setDraggedMemberId(null);
                                                    setDragOverTeam(undefined);
                                                }}
                                                className={`rounded-2xl border bg-[#FBFCFE] dark:bg-[#20242c] overflow-hidden transition ${isDropTarget ? 'border-blue-400 ring-2 ring-blue-300/40' : 'border-[#E2E8F0] dark:border-gray-800'}`}
                                            >
                                                {confirmingId != null && confirmingId === team.id ? (
                                                    <DeleteTeamConfirm
                                                        team={team}
                                                        staffCount={manageStaff.filter((s) => String(s.group_id ?? '') === String(team.id)).length}
                                                        onCancel={() => setConfirmingId(null)}
                                                        onDeleted={() => setConfirmingId(null)}
                                                    />
                                                ) : (
                                                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E2E8F0] dark:border-gray-800">
                                                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: team.color }} />
                                                        <strong className="text-[12px] font-extrabold tracking-[0.1em] text-[#1E293B] dark:text-gray-200 uppercase truncate">{team.name}</strong>
                                                        <span className="px-1.5 py-0.5 rounded-md bg-white dark:bg-[#31373f] text-[#66768c] dark:text-gray-400 text-[9px] font-extrabold">{members.length}</span>
                                                        {/* Unassigned is not a team — it can't be deleted. */}
                                                        {team.id !== '' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setConfirmingId(team.id)}
                                                                title={`Delete ${team.name}`}
                                                                aria-label={`Delete ${team.name}`}
                                                                className="ml-auto w-7 h-7 rounded-md grid place-items-center text-[#94A3B8] hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {members.length === 0 ? (
                                                    <div className={`py-5 text-center text-[12px] ${isDropTarget ? 'text-blue-500' : 'text-[#94A3B8] dark:text-gray-500'}`}>{isDropTarget ? 'Drop to move here' : 'No staff here. Drag a carer in.'}</div>
                                                ) : (
                                                    <div className="divide-y divide-[#EDF1F6] dark:divide-gray-800">
                                                        {members.map((s) => (
                                                            <div
                                                                key={s.id}
                                                                draggable
                                                                onDragStart={(e) => { setDraggedMemberId(s.id); e.dataTransfer.effectAllowed = 'move'; }}
                                                                onDragEnd={() => { setDraggedMemberId(null); setDragOverTeam(undefined); }}
                                                                className={`flex items-center gap-3 px-4 py-2.5 cursor-move select-none ${draggedMemberId === s.id ? 'opacity-40' : 'hover:bg-white dark:hover:bg-[#31373f]'}`}
                                                            >
                                                                <span className="text-[#C2CCD9] dark:text-gray-600 text-[14px] leading-none shrink-0" title="Drag to move">⋮⋮</span>
                                                                <InitialsAvatar name={s.name} size="md" />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200 truncate">{s.name}</div>
                                                                    <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                                                                        {s.user_id || '—'}{s.shift_name ? ` · ${s.shift_name}` : ''}
                                                                    </div>
                                                                </div>
                                                                <Select
                                                                    className="shrink-0 w-[170px]"
                                                                    size="sm"
                                                                    value={manageMoves[s.id] ?? ''}
                                                                    onChange={(v) => setMemberTeam(s.id, v === '' ? '' : Number(v))}
                                                                    options={[{ value: '', label: 'Unassigned' }, ...groups.map((g) => ({ value: String(g.id), label: g.name }))]}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {manageStaff.length === 0 && (
                                        <div className="py-10 text-center text-[13px] text-[#94A3B8] dark:text-gray-500">No staff found.</div>
                                    )}
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-2.5 pt-1">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={closeManageTeams}
                                    disabled={savingMoves}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={submitManageTeams}
                                    loading={savingMoves}
                                >
                                    Save changes
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>
    );
};

export default ManageTeamsModal;
